import { createContext } from 'react';

import type { SidekickFeatureEventInput } from './types';

/** Reports one feature event to the host. */
export type EmitSidekickFeatureEvent = (
  event: SidekickFeatureEventInput
) => void;

/** A question submission whose event waits for the question to land. */
export type SidekickQuestionSubmission = {
  /** The question being submitted. */
  question: string;
  /** Submits the question; returns `false` when it was refused. */
  submit: () => boolean;
};

/** The provider's reporting API. */
export type SidekickFeatureEventReporter = {
  /** Reports an event now. */
  emit: EmitSidekickFeatureEvent;
  /**
   * Binds an emitter to the host callback current at the call, so an
   * action that finishes later is reported with the host context it
   * started in, even after the host swaps its callback.
   */
  captureEmit: () => EmitSidekickFeatureEvent;
  /**
   * Runs `submit` and reports the event once the submitted question
   * lands in the chat; a refused or failed submission reports nothing.
   */
  emitOnQuestionSent: (
    event: SidekickFeatureEventInput,
    submission: SidekickQuestionSubmission
  ) => void;
};

/**
 * The provider's reporter, or `null` outside one, where feature events
 * go nowhere. Read it through `useSidekickFeatureEvent` or
 * `useSidekickFeatureEventReporter`.
 */
export const SidekickFeatureEventsContext =
  createContext<SidekickFeatureEventReporter | null>(null);
